/**
 * Offline / sync status chip + entry point to the "Sync now" dialog (desktop only).
 *
 * Subscribes to the main-process `sync:status` broadcasts and shows a small fixed
 * corner chip describing the current state (synced / N to sync / syncing / offline).
 * Clicking it opens {@link SyncDialog}, where the user can review the per-table
 * breakdown and push all local changes to the server with a live percentage.
 */

import React, { useEffect, useState } from 'react';
import { Chip, Tooltip, CircularProgress } from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { isDesktop } from '../setup/desktopApi';
import SyncDialog from './SyncDialog';

export default function SyncStatus() {
  const enabled = isDesktop() && !!(window.electron && window.electron.sync);
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    const unsub = window.electron.sync.onStatus((s) => setStatus(s));
    return unsub;
  }, [enabled]);

  if (!enabled || !status) return null;

  const { online, pending, syncing, failed = 0 } = status;

  let icon;
  let label;
  let color;
  let tooltip = 'Sync now — review and send your local changes to the server';
  if (syncing) {
    icon = <CircularProgress size={14} color="inherit" />;
    label = `Syncing${pending ? ` · ${pending}` : '…'}`;
    color = 'warning';
  } else if (failed) {
    // Ranked above offline: these need the user's attention; being offline does not.
    icon = <ErrorOutlineIcon />;
    label = `${failed} failed`;
    color = 'error';
    tooltip = 'Some changes could not be sent — open to review, retry or export them';
  } else if (!online) {
    icon = <CloudOffIcon />;
    label = pending ? `Offline · ${pending}` : 'Offline';
    color = 'default';
  } else if (pending) {
    icon = <CloudSyncIcon />;
    label = `${pending} to sync`;
    color = 'warning';
  } else {
    icon = <CloudDoneIcon />;
    label = 'Synced';
    color = 'success';
  }

  return (
    <>
      <Tooltip title={tooltip}>
        <Chip
          icon={icon}
          label={label}
          color={color}
          size="small"
          onClick={() => setOpen(true)}
          sx={{ position: 'fixed', bottom: 12, right: 12, zIndex: 1500, boxShadow: 2, cursor: 'pointer' }}
        />
      </Tooltip>
      <SyncDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
